import { useState, useCallback } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { VersionedTransaction } from '@solana/web3.js'
import { preparePayment, submitPayment } from '@/services/api'

export type PaymentStatus =
  | 'idle'
  | 'preparing'
  | 'signing'
  | 'submitting'
  | 'success'
  | 'error'

interface PaymentResult {
  signature: string
  explorerUrl: string
}

interface UsePaymentResult {
  status: PaymentStatus
  error: string | null
  result: PaymentResult | null
  pay: (paymentToken: string, token?: string) => Promise<void>
  reset: () => void
}

export function usePayment(): UsePaymentResult {
  const { publicKey, signTransaction } = useWallet()
  const [status, setStatus] = useState<PaymentStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<PaymentResult | null>(null)

  const pay = useCallback(
    async (paymentToken: string, token: string = 'USDC') => {
      if (!publicKey || !signTransaction) {
        setError('Please connect your wallet first')
        setStatus('error')
        return
      }

      try {
        setStatus('preparing')
        setError(null)
        setResult(null)

        // 1. Prepare the transaction
        const prepareResponse = await preparePayment(paymentToken, {
          payerWallet: publicKey.toBase58(),
          paymentToken: token,
        })

        // 2. Deserialize the versioned transaction
        const transaction = VersionedTransaction.deserialize(
          Buffer.from(prepareResponse.transaction, 'base64')
        )

        // 3. Sign the transaction
        setStatus('signing')
        const signedTransaction = await signTransaction(transaction)

        // 4. Serialize and submit
        setStatus('submitting')
        const serialized = Buffer.from(signedTransaction.serialize()).toString('base64')

        const submitResponse = await submitPayment(paymentToken, {
          signedTransaction: serialized,
          messageHash: prepareResponse.messageHash,
          blockhash: prepareResponse.blockhash,
        })

        // 5. Success!
        setResult({
          signature: submitResponse.signature,
          explorerUrl: submitResponse.explorerUrl,
        })
        setStatus('success')
      } catch (err: any) {
        console.error('Payment error:', err)
        
        // Try to extract error message from API response
        let message = 'Payment failed'
        
        if (err?.response) {
          // ky error with response
          try {
            const errorData = await err.response.json()
            message = errorData?.error || errorData?.message || message
          } catch {
            message = err.message || message
          }
        } else if (err?.message) {
          message = err.message
        }
        
        setError(message)
        setStatus('error')
      }
    },
    [publicKey, signTransaction]
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setError(null)
    setResult(null)
  }, [])

  return { status, error, result, pay, reset }
}
